import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';

import { SignatureService } from 'src/app/data/signature.service';
import { CompleteSignatureRequest } from 'src/app/contracts/requests/complete-signature-request';
import { CompleteSignatureResponse } from 'src/app/contracts/responses/complete-signature-response';
import { DocumentViewerService } from 'src/app/shared/services/document-viewer.service';

import { ViewState, OAuthErrorCode, SignatureErrorCode, QueryParamKey } from './signature-callback.enums';
import { TRANSLATION_KEYS, CONFIG } from './signature-callback.constants';

@Component({
  selector: 'app-signature-callback',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxSpinnerModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatListModule,
  ],
  templateUrl: './signature-callback.component.html',
  styleUrls: ['./signature-callback.component.scss'],
})
export class SignatureCallbackComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private signatureService = inject(SignatureService);
  private spinner = inject(NgxSpinnerService);
  private formBuilder = inject(FormBuilder);
  private documentViewerService = inject(DocumentViewerService);

  private unsubscribe$ = new Subject<void>();

  readonly ViewState = ViewState;

  viewState: ViewState = ViewState.Loading;

  pinForm: FormGroup;
  hidePin = true;
  isSubmitting = false;

  sessionId: number;
  fileIds: number[] = [];
  code: string;
  
  remainingAttempts: number | null = null;
  
  errorMessage: string;
  successMessage: string;
  pinErrorMessage: string;

  signedFilesCount: number;
  signedFileUrls: any[] = [];

  ngOnInit(): void {
    this.initializePinForm();
    this.spinner.show();
    
    this.route.queryParams
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        this.code = params[QueryParamKey.Code];
        const error = params[QueryParamKey.Error];
        const errorDescription = params[QueryParamKey.ErrorDescription];
        this.sessionId = params[QueryParamKey.SessionId];
        const fileIdsParam = params[QueryParamKey.FileIds];

        if (error) {
          this.handleOAuthError(error, errorDescription);
          return;
        }

        if (!this.sessionId) {
          this.showErrorState(this.translateService.instant(TRANSLATION_KEYS.MissingSession));
          return;
        }

        if (fileIdsParam) {
          try {
            this.fileIds = JSON.parse(fileIdsParam);
          } catch {
            this.fileIds = fileIdsParam.split(',').map(id => parseInt(id, 10));
          }
        }

        this.viewState = ViewState.PinEntry;
        this.spinner.hide();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializePinForm(): void {
    this.pinForm = this.formBuilder.group({
      pin: ['', [
        Validators.required, 
        Validators.minLength(CONFIG.PIN_MIN_LENGTH), 
        Validators.maxLength(CONFIG.PIN_MAX_LENGTH), 
        Validators.pattern(CONFIG.PIN_PATTERN)
      ]]
    });
  }

  private handleOAuthError(error: string, description: string): void {
    let message: string;

    switch (error) {
      case OAuthErrorCode.AccessDenied:
        message = this.translateService.instant(TRANSLATION_KEYS.AccessDenied);
        break;
      case OAuthErrorCode.InvalidRequest:
        message = this.translateService.instant(TRANSLATION_KEYS.InvalidRequest);
        break;
      default:
        message = this.translateService.instant(TRANSLATION_KEYS.UnknownError, { error: description || error });
    }

    this.showErrorState(message);
  }

  async onSubmitPin(): Promise<void> {
    if (this.pinForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.pinErrorMessage = null;
    this.spinner.show();

    const pin = this.pinForm.get('pin').value;
    
    const request: CompleteSignatureRequest = {
      SessionId: this.sessionId,
      Pin: pin,
    };

    try {
      const response = await firstValueFrom(this.signatureService.completeSignature(request));

      if (response.Success) {
        this.handleSuccess(response);
      } else {
        this.handleSignatureError(response);
      }
    } catch (error: any) {
      this.isSubmitting = false;
      this.spinner.hide();

      const errorBody = error?.error;
      const errorCode = errorBody?.ErrorCode || error?.ErrorCode;
      
      if (errorCode) {
        const errorMessage = this.getUserFriendlyErrorMessage(errorCode);
        this.showErrorState(errorMessage);
      } else if (errorBody) {
        const errorMessage = this.extractErrorMessage(errorBody);
        this.showErrorState(errorMessage);
      } else {
        this.showErrorState(this.translateService.instant(TRANSLATION_KEYS.NetworkError));
      }
    }
  }

  private handleSuccess(response: CompleteSignatureResponse): void {
    this.isSubmitting = false;
    this.spinner.hide();

    this.signedFilesCount = response.SignedFiles?.length || 0;
    this.signedFileUrls = response.SignedFiles || [];
    
    this.successMessage = this.translateService.instant(TRANSLATION_KEYS.SuccessMessage);

    this.viewState = ViewState.Success;
  }

  showFile(file: any): void {
    if (!file?.FileId || !file?.FileName) {
      return;
    }

    this.documentViewerService.show({
      Endpoint: 'employmentConditionsConfirmation/files',
      FileId: file.FileId,
      FileName: file.FileName,
    });
  }

  private handleSignatureError(response: CompleteSignatureResponse): void {
    this.isSubmitting = false;
    this.spinner.hide();

    const errorCode: string = response?.ErrorCode;
    this.remainingAttempts = response?.RemainingAttempts;
    
    const isPinError = errorCode === SignatureErrorCode.InvalidPin;

    if (isPinError) {
      if (this.remainingAttempts !== null && this.remainingAttempts <= 0) {
        const message = this.translateService.instant(TRANSLATION_KEYS.MaxAttemptsExceeded);
        this.showErrorState(message);
        return;
      }

      this.pinErrorMessage = this.getUserFriendlyErrorMessage(errorCode);
      this.pinForm.reset();
      
      this.viewState = ViewState.PinEntry;
      return;
    }

    const nonPinMessage = this.getUserFriendlyErrorMessage(errorCode) || 
                          this.translateService.instant(TRANSLATION_KEYS.SignatureError);
    this.showErrorState(nonPinMessage);
  }

  private getUserFriendlyErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case SignatureErrorCode.InvalidPin:
        return this.translateService.instant(TRANSLATION_KEYS.InvalidPin, { remaining: this.remainingAttempts ?? 0 });
      case SignatureErrorCode.PinLocked:
        return this.translateService.instant(TRANSLATION_KEYS.PinLocked);
      case SignatureErrorCode.SessionExpired:
        return this.translateService.instant(TRANSLATION_KEYS.SessionExpired);
      case SignatureErrorCode.SessionNotFound:
        return this.translateService.instant(TRANSLATION_KEYS.SessionNotFound);
      case SignatureErrorCode.SessionInvalidStatus:
        return this.translateService.instant(TRANSLATION_KEYS.SessionInvalidStatus);
      default:
        return this.translateService.instant(TRANSLATION_KEYS.SignatureError);
    }
  }

  private extractErrorMessage(errorBody: any): string {
    const errorCode = typeof errorBody === 'string' ? errorBody : errorBody?.Message || errorBody?.message;
    
    if (errorCode) {
      return this.getUserFriendlyErrorMessage(errorCode);
    }
    
    return this.translateService.instant(TRANSLATION_KEYS.SignatureError);
  }

  private showErrorState(message: string): void {
    this.errorMessage = message;
    this.viewState = ViewState.Error;
    this.spinner.hide();
  }

  onNavigate(): void {
    this.router.navigate(['/workerAgreements'], { queryParams: { tab: 'employment-conditions-confirmations' } });
  }

  onCancel(): void {
    this.router.navigate(['/workerAgreements'], { queryParams: { tab: 'employment-conditions-confirmations' } });
  }

  get pin() {
    return this.pinForm.get('pin');
  }

  get pinValidationErrorMessage(): string {
    if (this.pin.hasError('required')) {
      return this.translateService.instant(TRANSLATION_KEYS.ValidationRequired);
    }
    if (this.pin.hasError('pattern')) {
      return this.translateService.instant(TRANSLATION_KEYS.ValidationPattern);
    }
    if (this.pin.hasError('minlength') || this.pin.hasError('maxlength')) {
      return this.translateService.instant(TRANSLATION_KEYS.ValidationLength);
    }
    return '';
  }
}
